/* eslint-disable react/require-default-props */
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend,
  Cell,
  Label,
} from 'recharts';
import CustomTooltip from './CustomTooltip';

type Props = {
  data: any[] | null | undefined;
  chartTitle?: string;
  dataKey?: string;
  withTooltip?: boolean;
  xAxisKey?: string;
  dateRange?: string;
  type?: 'dollar' | 'number';
  error?: boolean | null; // The error message or null if no error
  chartTotalValue?: string;
};

export default function CustomPieChart({
  data,
  dataKey: passedDataKey,
  withTooltip,
  xAxisKey,
  chartTitle,
  dateRange,
  type,
  error,
  chartTotalValue,
}: Props) {
  const theme = useTheme();

  const colors = [
    theme.palette.secondary.main,
    theme.palette.accent.main,
    theme.palette.primary.main,
    theme.palette.primary.light,
  ];

  // Handle loading state
  if (data === null || data === undefined) {
    return <Skeleton variant="rectangular" width="100%" height={200} />;
  }

  // Handle error state
  if (error) {
    return (
      <div>
        <Typography variant="body2">Error loading report data</Typography>
      </div>
    );
  }

  const assignedColors: Record<string, string> = {};

  const nameKey = xAxisKey || 'name';
  const dataKey = passedDataKey || 'value';

  const getColor = (entry: any) => {
    // If color is already assigned, return it
    if (assignedColors[entry[nameKey]]) {
      return assignedColors[entry[nameKey]];
    }

    // If color is specified in the entry itself, assign and return it
    if (entry.color) {
      assignedColors[entry[nameKey]] = entry.color;
      return entry.color;
    }

    // If no color is specified, assign a color from defaultColors
    const availableColors = colors.filter(
      color => !Object.values(assignedColors).includes(color),
    );
    if (availableColors.length > 0) {
      const selectedColor = availableColors[0];
      assignedColors[entry[nameKey]] = selectedColor;
      return selectedColor;
    }

    // If no available color, default to a fallback (unlikely to be used if there are less than 20 unique entries)
    return theme.palette.secondary.light;
  };
  // eslint-disable-next-line react/no-unstable-nested-components
  function RenderLegend(props: any) {
    const { payload } = props;

    // Utility function to format the text
    const formatLegendText = (text: string) =>
      // Split the string on underscores, capitalize the first letter of each word, then join with spaces
      text
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

    return (
      <ul>
        {payload.map((entry: any, index: number) => (
          <li
            // eslint-disable-next-line react/no-array-index-key
            key={`item-${index}`}
            style={{
              listStyleType: 'none',
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
            <svg
              width="30"
              height="30"
              viewBox="0 0 32 32"
              style={{ marginRight: '0px' }}>
              <circle cx="16" cy="16" r="8" fill={entry.color} />
            </svg>
            <span style={{ color: '#333333' }}>
              {formatLegendText(entry.value)}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Box>
      {chartTitle && (
        <Box
          mb={2}
          style={{
            borderBottomColor: theme.palette.lightGrey.main,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            paddingBottom: 10,
            marginBottom: 10,
            marginTop: 40,
          }}>
          <Typography variant="h3">{chartTitle}</Typography>
          {dateRange && <Typography variant="body2">{dateRange}</Typography>}
        </Box>
      )}
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={xAxisKey}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}>
            {data.map((entry, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Cell key={`cell-${index}`} fill={getColor(entry)} />
            ))}
            {chartTotalValue && (
              <Label
                value={chartTotalValue}
                position="center"
                fill={theme.palette.darkGrey.main}
                style={{
                  fontSize: theme.typography.h5.fontSize,
                  fontWeight: theme.typography.h5.fontWeight,
                  color: theme.typography.h5.color,
                  fontFamily: theme.typography.fontFamily,
                }}
              />
            )}
          </Pie>
          {withTooltip && (
            <Tooltip
              content={
                <CustomTooltip
                  chartTitle={chartTitle}
                  dataKey={dataKey}
                  type={type || 'number'}
                  xAxisKey={xAxisKey}
                />
              }
            />
          )}
          <Legend
            content={<RenderLegend />}
            layout="vertical"
            verticalAlign="middle"
            align="left"
            wrapperStyle={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
              color: theme.typography.body2.color,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}
